import { UserDetail, Unit, SurveyResponse, LoginResponse } from '../documentation/User';
import { Permissions } from '../documentation/Permission';

import {
  EXP,
  HOSPITALIZATION_AFFILIATE_VALIDATION,
  LOCALSTORAGE_AUTH_KEY,
  TOKEN,
  USER_DATA,
  USER_HAS_ACCEPTED_TERMS,
  USER_HAS_DOC24_PERMISSION,
  USER_IS_CIRCLE,
  USER_MANAGEMENT_UNITS,
  USER_PERMISSIONS,
  USER_SURVEY_RESPONSE,
} from '../constants/localStorage';
import LocalStorageService from '../services/LocalStorage/LocalStorage.service';
import Encryption from '../services/Encryption/Encryption.service';
import Dates from './dates';

const Session = {
  /**
   * @param {Date} expDate
   * @returns {Boolean}
   */
  checkSessionTokenExpiration(expDate) {
    const now = new Date();
    return Dates.get(expDate).compareIsAfter(now);
  },
  /**
   * @returns {String}
   */
  getTokenExpirationTime() {
    const exp = LocalStorageService.get(EXP);

    if (exp) return exp;
  },
  /**
   * @returns {String}
   */
  getAuthenticationToken() {
    const token = LocalStorageService.get(TOKEN);

    if (token) return token;
  },
  updateAuthenticationToken(newToken, newExp) {
    LocalStorageService.remove(TOKEN);
    LocalStorageService.remove(EXP);
    LocalStorageService.set(TOKEN, newToken);
    LocalStorageService.set(EXP, newExp);
  },
  /**
   * @returns {UserDetail}
   */
  getAuth() {
    try {
      const session = LocalStorageService.get(LOCALSTORAGE_AUTH_KEY);
      if (session) return Encryption.decrypt(session);
    } catch (error) {
      Session.remove();
    }
  },
  updateAuth(data) {
    LocalStorageService.set(LOCALSTORAGE_AUTH_KEY, Encryption.encrypt(data));
  },
  save(
    data,
    token,
    exp,
    userPermissions,
    userHasAcceptedTerms,
    userHasDOC24Permission,
    userSurveyResponse,
    isCircle,
    managementUnit,
    hospitalizationInAffiliateValidation,
  ) {
    LocalStorageService.set(LOCALSTORAGE_AUTH_KEY, Encryption.encrypt(data));
    LocalStorageService.set(TOKEN, token);
    LocalStorageService.set(EXP, exp);
    LocalStorageService.set(USER_PERMISSIONS, userPermissions);
    LocalStorageService.set(USER_HAS_ACCEPTED_TERMS, userHasAcceptedTerms);
    LocalStorageService.set(USER_HAS_DOC24_PERMISSION, userHasDOC24Permission);
    LocalStorageService.set(USER_SURVEY_RESPONSE, userSurveyResponse);
    LocalStorageService.set(USER_IS_CIRCLE, isCircle);
    LocalStorageService.set(USER_MANAGEMENT_UNITS, managementUnit);
    LocalStorageService.set(
      HOSPITALIZATION_AFFILIATE_VALIDATION,
      hospitalizationInAffiliateValidation ?? false,
    );
  },
  remove() {
    LocalStorageService.remove(TOKEN);
    LocalStorageService.remove(EXP);
    LocalStorageService.remove(LOCALSTORAGE_AUTH_KEY);
    LocalStorageService.remove(USER_PERMISSIONS);
    LocalStorageService.remove(USER_HAS_ACCEPTED_TERMS);
    LocalStorageService.remove(USER_HAS_DOC24_PERMISSION);
    LocalStorageService.remove(USER_SURVEY_RESPONSE);
    LocalStorageService.remove(USER_IS_CIRCLE);
    LocalStorageService.remove(USER_MANAGEMENT_UNITS);
    LocalStorageService.remove(HOSPITALIZATION_AFFILIATE_VALIDATION);
  },
  /**
   * @returns {LoginResponse}
   */
  getExternalAuth() {
    return LocalStorageService.get(USER_DATA);
  },
  removeExternalAuth() {
    LocalStorageService.remove(USER_DATA);
  },
  /**
   * @returns {Permissions}
   */
  getPermissions() {
    return LocalStorageService.get(USER_PERMISSIONS);
  },
  /**
   * @returns {Boolean}
   */
  getAcceptedTerms() {
    return LocalStorageService.get(USER_HAS_ACCEPTED_TERMS);
  },
  updateAcceptedTerms(value) {
    LocalStorageService.set(USER_HAS_ACCEPTED_TERMS, value);
  },
  /**
   * @returns {Number}
   */
  getDoc24Permission() {
    return LocalStorageService.get(USER_HAS_DOC24_PERMISSION);
  },
  /**
   * @returns {Boolean}
   */
  getUserIsCircle() {
    return LocalStorageService.get(USER_IS_CIRCLE);
  },
  /**
   * @returns {SurveyResponse}
   */
  getSurveyResponse() {
    return LocalStorageService.get(USER_SURVEY_RESPONSE);
  },
  /**
   * @returns {Unit[]}
   */
  getManagementUnits() {
    return LocalStorageService.get(USER_MANAGEMENT_UNITS);
  },
  getMillisecondsToExpire(substractMinutes) {
    const expTime = LocalStorageService.get(EXP);
    const now = new Date();

    return Dates.getWithSubstractMinutes(substractMinutes, expTime).getDifference(now);
  },
  /**
   * @returns {Boolean}
   */
  getHospitalizationInAffiliateValidation() {
    return LocalStorageService.get(HOSPITALIZATION_AFFILIATE_VALIDATION);
  },
};

export default Session;
